import { type VariableDefinition, useUnavailableVariableIds } from "@babackapp/variables-registry";
import { cn } from "@babackapp/ui/lib/utils";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useMemo } from "react";
import { parseMentionsFromText } from "../utils/parseMentionsFromText";
import createVariablesMentionExtension from "@babackapp/editor-blocks-library/mention-extension";

const HARD_BREAK_TEXT_SERIALIZER = { hardBreak: () => "\n" };

interface BlockEditorInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  variables: VariableDefinition[];
  className?: string;
  outputFormat?: "html" | "text";
}

export function BlockEditorInput({
  value = "",
  onChange,
  placeholder,
  variables,
  className,
  outputFormat = "html",
}: BlockEditorInputProps) {
  const isTextMode = outputFormat === "text";
  const unavailableVariableIds = useUnavailableVariableIds();

  const extensions = useMemo(
    () => [
      Document,
      createVariablesMentionExtension(variables, unavailableVariableIds),
      Paragraph,
      Text,
      ...(isTextMode ? [HardBreak] : []),
    ],
    [variables, isTextMode, unavailableVariableIds],
  );

  const editor = useEditor({
    extensions,
    content: value,
    editorProps: {
      attributes: {
        class: cn(
          "min-h-9 px-3 py-2 text-sm rounded-md border bg-background outline-none ring-0 focus:outline-none focus:ring-0",
          className,
        ),
        style: "white-space: pre-wrap;",
        placeholder: placeholder || "",
      },
    },
    onUpdate: ({ editor }) => {
      if (onChange) {
        const content = isTextMode
          ? editor.getText({ textSerializers: HARD_BREAK_TEXT_SERIALIZER })
          : editor.getHTML();
        onChange(content);
      }
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && !editor.isFocused) {
      if (value.includes("{{") && value.includes("}}")) {
        const parsedContent = parseMentionsFromText(value, variables, {
          useHardBreaks: isTextMode,
        });
        const currentJSON = JSON.stringify(editor.getJSON());
        const newJSON = JSON.stringify(parsedContent);
        if (currentJSON !== newJSON) {
          editor.commands.setContent(parsedContent, { emitUpdate: false });
        }
      } else {
        const currentContent = isTextMode
          ? editor.getText({ textSerializers: HARD_BREAK_TEXT_SERIALIZER })
          : editor.getHTML();
        if (currentContent !== value) {
          editor.commands.setContent(value, { emitUpdate: false });
        }
      }
    }
  }, [editor, value, isTextMode, variables]);

  return <EditorContent editor={editor} />;
}
